import { put, delay, takeEvery, all, call, select, race, cancelled } from "redux-saga/effects";
import _ from 'lodash';
import moment from 'moment-timezone';
import { getJwtToken, getDataFromKpiDash, getDataFromSxi } from "api";
import { PICTREE, ENDPOINT } from "../../constants";

import {
  setPageTopLoader,
  setPhotoShootRankingData,
  setPhotoFirstShootRankingData
} from 'actions';

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000,   // 30s
  retries: 3,         // 合計 4 回までトライ（初回 + リトライ3）
  backoffBase: 1500,  // 1.5s指数バックオフ
};

// タイムアウト＋リトライ
function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000,           // 1回の待ち時間（APIGW上限より少し短く）
    retries = 2,                 // 失敗時の再試行回数（合計3回＝初回＋2リトライ）
    backoffBase = 1000,          // 1秒
    backoffFactor = 2,           // 2倍ずつ
    jitter = true,               // 少しランダムに待つ
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || '';
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    }
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      // 1回の試行にタイムアウトをかける
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) throw new Error(`timeout after ${timeoutMs}ms`);
      return response; // 成功

    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err; // もう無理/リトライ不要
      }
      // 待ってから再試行（指数＋ジッター）
      const wait = Math.min(backoffBase * Math.pow(backoffFactor, attempt - 1), 10000);
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
      // 次のループで再試行
    } finally {
      if (yield cancelled()) {
      }
    }
  }
}

export const dateRange = (state) => state.page.dateRangePicTree;
export const timeRange = (state) => state.page.timeRangePicTree;
export const selectedSeason = state => state.pictree.selectedSeason;

function* handlePhotoShootRankingData(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const timeRangeTarget = yield select(timeRange);
    const selectedSeasonData = yield select(selectedSeason);

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.error("No seasons selected");
      yield put(setPageTopLoader(false));
      return;
    }

    const seasonQuery = (() => {
      if (
        selectedSeasonData.length > 0 &&
        selectedSeasonData[0].seasonId !== undefined
      ) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `AND rr.game_space_id = ${ids}` // ids は "'id1'"
          : `AND rr.game_space_id IN (${ids})`; // ids は "'id1','id2',…"
      }
      return "";
    })();

    const sqlQueryPhotoShootRanking = {
      query: `
        SELECT rr.player_id, ps.name, COUNT(*) as achievedNum
        FROM review_request_event rre
        INNER JOIN review_request rr ON rre.review_request_id = rr.id
        INNER JOIN player_state ps ON ps.player_id = rr.player_id
        INNER JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
        WHERE rre.type IN ('APPROVED') ${seasonQuery}
        AND CONVERT_TZ(rre.occurred_datetime, 'UTC', 'Asia/Tokyo')
        BETWEEN '${rangeTarget[0]} ${timeRangeTarget[0]}:00' AND '${rangeTarget[1]} ${timeRangeTarget[1]}:00'
        GROUP BY ps.player_id
        ORDER BY achievedNum DESC;
      `
    }

    const isTodayJP = moment(rangeTarget[0]).isSame(moment().tz('Asia/Tokyo').format('YYYY-MM-DD'), 'day');

    let dataRankingTemp;

    if (isTodayJP) {
      // 🔴 本日なら本番DB
      [dataRankingTemp] = yield all([
        call(getDataFromKpiDash, sqlQueryPhotoShootRanking, ENDPOINT.GET_DATA_API),
      ]);
    } else {
      // 🟢 本日以外はSXIレプリカ参照
      [dataRankingTemp] = yield all([
        // call(getDataFromSxi, sqlQueryPhotoShootRanking, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
        call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [sqlQueryPhotoShootRanking, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        ),
      ]);
    }

    const rankingData = isTodayJP ? JSON.parse(dataRankingTemp.body) : dataRankingTemp;
    let rankingTableData = [];
    _.each(rankingData, function(d, index) {
      rankingTableData.push([
        index+1,
        d.player_id,
        d.name,
        d.achievedNum
      ]);
    })
    yield put(setPhotoShootRankingData(rankingTableData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.error("Error fetching photo shoot ranking data:", error);
  }
}

function* handlePhotoFirstShootRankingData(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const timeRangeTarget = yield select(timeRange);
    const selectedSeasonData = yield select(selectedSeason);

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.error("No seasons selected");
      yield put(setPageTopLoader(false));
      return;
    }

    const seasonQuery = (() => {
      if (
        selectedSeasonData.length > 0 &&
        selectedSeasonData[0].seasonId !== undefined
      ) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `rr.game_space_id = ${ids}` // ids は "'id1'"
          : `rr.game_space_id IN (${ids})`; // ids は "'id1','id2',…"
      }
      return "";
    })();

    const query = {
      query: `
        SELECT rr.player_id, ps.name, COUNT(*) as achievedNum
        FROM review_request_event rre
        JOIN review_request rr ON rre.review_request_id = rr.id
        JOIN player_state ps ON ps.player_id = rr.player_id
        JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
        WHERE ${seasonQuery}
        AND rreoa.review_approved_count = '1'
        AND CONVERT_TZ(rre.occurred_datetime, 'UTC', 'Asia/Tokyo')
        BETWEEN '${rangeTarget[0]} ${timeRangeTarget[0]}:00' AND '${rangeTarget[1]} ${timeRangeTarget[1]}:00'
        GROUP BY rr.player_id
        ORDER BY achievedNum DESC;
      `
    }

    const isTodayJP = moment(rangeTarget[0]).isSame(moment().tz('Asia/Tokyo').format('YYYY-MM-DD'), 'day');

    let dataRankingTemp;

    if (isTodayJP) {
      // 🔴 本日なら本番DB
      [dataRankingTemp] = yield all([
        call(getDataFromKpiDash, query, ENDPOINT.GET_DATA_API),
      ]);
    } else {
      // 🟢 本日以外はSXIレプリカ参照
      [dataRankingTemp] = yield all([
        // call(getDataFromSxi, query, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
        call(
          callWithTimeoutAndRetry,
          getDataFromSxi,
          [query, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        ),
      ]);
    }

    const rankingData = isTodayJP ? JSON.parse(dataRankingTemp.body) : dataRankingTemp;
    let rankingTableData = [];
    _.each(rankingData, function(d, index) {
      rankingTableData.push([
        index+1,
        d.player_id,
        d.name,
        d.achievedNum
      ]);
    })
    yield put(setPhotoFirstShootRankingData(rankingTableData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.error("Error fetching photo shoot ranking data:", error);
  }
}

export default function* watchMapSettings() {
  yield takeEvery(PICTREE.GET_PHOTO_SHOOT_RANKING_DATA, handlePhotoShootRankingData);
  yield takeEvery(PICTREE.GET_PHOTO_FIRST_SHOOT_RANKING_DATA, handlePhotoFirstShootRankingData);
}